import React, { useEffect, useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

// ──────────────────────────────────────────────────────────────────
// Magnetic wrapper — subtly drifts its child toward the cursor when
// the pointer is inside its bounding box. Perfect for hero CTAs.
//
// Usage:
//   <Magnetic><Link ...>Request a Quote</Link></Magnetic>
//
// Props:
//   strength — 0.15–0.35 recommended. Default 0.18 keeps drift under 4px
//              for a ~180px wide button.
//   className — applied to the outer wrapper (inline-block by default).
// ──────────────────────────────────────────────────────────────────
export default function Magnetic({
  children,
  strength = 0.18,
  className = "",
}) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 22, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 260, damping: 22, mass: 0.4 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!mq.matches) return undefined;

    const handleMove = (e) => {
      const r = el.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      x.set((e.clientX - cx) * strength);
      y.set((e.clientY - cy) * strength);
    };
    const reset = () => {
      x.set(0);
      y.set(0);
    };
    el.addEventListener("mousemove", handleMove);
    el.addEventListener("mouseleave", reset);
    return () => {
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("mouseleave", reset);
    };
  }, [strength, x, y]);

  return (
    <motion.span
      ref={ref}
      style={{ x: sx, y: sy, display: "inline-block" }}
      className={className}
    >
      {children}
    </motion.span>
  );
}
